import React, { useState, useEffect, useMemo } from 'react'
import axios from 'axios'
import {
  DataGrid,
  GridColDef,
  GridFilterModel,
  useGridApiRef,
} from '@mui/x-data-grid'

import {
  Box,
  Button,
  TextField,
  Typography,
  CircularProgress,
  Alert,
  Stack,
  Tooltip,
} from '@mui/material'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import SearchIcon from '@mui/icons-material/Search'
import DownloadIcon from '@mui/icons-material/Download'
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff'

interface TeacherData {
  id: string
  code: string
  email: string
  name: string
  firstName: string
  lastName: string
  title: string
  school: string
  classes: number
  creationAt: string
  lastActivity: string
  bouncing: string
  licenseCode: string
  licenseType: string
  licenseRole: string
  licenseName: string
  licenseExp: string
  license2Code: string
  license2Type: string
  license2Role: string
  license2Name: string
  license2Exp: string
}

const STORAGE_KEY_FILTERS = 'domainSearch_filters'
const STORAGE_KEY_COLUMNS = 'domainSearch_columns'

const getParentOrigin = () => {
  try {
    return document.referrer
      ? new URL(document.referrer).origin
      : window.location.origin
  } catch {
    return window.location.origin
  }
}

const getParentUrlParam = (param: string): string | null => {
  try {
    const parentUrl = new URL(window.parent.location.href)
    return parentUrl.searchParams.get(param)
  } catch {
    // Cross-origin or other error - try reading from referrer
    try {
      const referrerUrl = new URL(document.referrer)
      return referrerUrl.searchParams.get(param)
    } catch {
      return null
    }
  }
}

const updateParentUrl = (param: string, value: string | null) => {
  try {
    const parentUrl = new URL(window.parent.location.href)
    if (value) {
      parentUrl.searchParams.set(param, value)
    } else {
      parentUrl.searchParams.delete(param)
    }
    window.parent.history.replaceState({}, '', parentUrl.toString())
  } catch {
    // Cross-origin or other error - silently fail
  }
}

const columns: GridColDef[] = [
  {
    field: 'code',
    headerName: 'Code',
    width: 140,
    filterable: true,
    renderCell: (params) => (
      <Button
        variant='text'
        size='small'
        onClick={() =>
          window.open(
            `${getParentOrigin()}/teacher?t=${params.value}`,
            '_blank'
          )
        }
        endIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
        sx={{
          textTransform: 'none',
          p: 0,
          minWidth: 'auto',
          '&:hover': {
            textDecoration: 'underline',
            backgroundColor: 'transparent',
          },
        }}
      >
        {params.value}
      </Button>
    ),
  },
  {
    field: 'email',
    headerName: 'Email',
    width: 200,
    filterable: true,
  },
  {
    field: 'name',
    headerName: 'Name',
    width: 200,
    filterable: true,
  },
  {
    field: 'firstName',
    headerName: 'First Name',
    width: 150,
    filterable: true,
  },
  {
    field: 'lastName',
    headerName: 'Last Name',
    width: 150,
    filterable: true,
  },
  {
    field: 'title',
    headerName: 'Title',
    width: 100,
    filterable: true,
  },
  {
    field: 'school',
    headerName: 'School',
    width: 200,
    filterable: true,
  },
  {
    field: 'classes',
    headerName: 'Classes',
    type: 'number',
    width: 100,
    filterable: true,
  },
  {
    field: 'creationAt',
    headerName: 'Creation Date',
    type: 'date',
    width: 130,
    filterable: true,
    valueGetter: (params) => {
      if (!params.row.creationAt) return null
      const parts = params.row.creationAt.split('/')
      if (parts.length !== 3) return null
      const month = parseInt(parts[0], 10) - 1
      const day = parseInt(parts[1], 10)
      const year = parseInt(parts[2], 10) + 2000
      return new Date(year, month, day)
    },
    valueFormatter: (params) => {
      if (!params.value) return ''
      const date = params.value as Date
      const month = date.getMonth() + 1
      const day = date.getDate()
      const year = date.getFullYear().toString().slice(-2)
      return `${month}/${day}/${year}`
    },
  },
  {
    field: 'lastActivity',
    headerName: 'Last Activity',
    type: 'date',
    width: 130,
    filterable: true,
    valueGetter: (params) => {
      if (!params.row.lastActivity) return null
      const parts = params.row.lastActivity.split('/')
      if (parts.length !== 3) return null
      const month = parseInt(parts[0], 10) - 1
      const day = parseInt(parts[1], 10)
      const year = parseInt(parts[2], 10) + 2000
      return new Date(year, month, day)
    },
    valueFormatter: (params) => {
      if (!params.value) return ''
      const date = params.value as Date
      const month = date.getMonth() + 1
      const day = date.getDate()
      const year = date.getFullYear().toString().slice(-2)
      return `${month}/${day}/${year}`
    },
  },
  {
    field: 'bouncing',
    headerName: 'Bouncing',
    width: 120,
    filterable: true,
  },
  {
    field: 'licenseCode',
    headerName: 'License Code',
    width: 170,
    filterable: true,
    renderCell: (params) =>
      params.value ? (
        <Button
          variant='text'
          size='small'
          onClick={() =>
            window.open(
              `${getParentOrigin()}/license?l=${params.value}`,
              '_blank'
            )
          }
          endIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
          sx={{
            textTransform: 'none',
            p: 0,
            minWidth: 'auto',
            '&:hover': {
              textDecoration: 'underline',
              backgroundColor: 'transparent',
            },
          }}
        >
          {params.value}
        </Button>
      ) : null,
  },
  {
    field: 'licenseType',
    headerName: 'License Type',
    width: 150,
    filterable: true,
  },
  {
    field: 'licenseRole',
    headerName: 'License Role',
    width: 130,
    filterable: true,
  },
  {
    field: 'licenseName',
    headerName: 'License Name',
    width: 200,
    filterable: true,
  },
  {
    field: 'licenseExp',
    headerName: 'License Exp',
    type: 'date',
    width: 130,
    filterable: true,
    valueGetter: (params) => {
      if (!params.row.licenseExp) return null
      const parts = params.row.licenseExp.split('/')
      if (parts.length !== 3) return null
      const month = parseInt(parts[0], 10) - 1
      const day = parseInt(parts[1], 10)
      const year = parseInt(parts[2], 10) + 2000
      return new Date(year, month, day)
    },
    valueFormatter: (params) => {
      if (!params.value) return ''
      const date = params.value as Date
      const month = date.getMonth() + 1
      const day = date.getDate()
      const year = date.getFullYear().toString().slice(-2)
      return `${month}/${day}/${year}`
    },
  },
  {
    field: 'license2Code',
    headerName: 'License 2 Code',
    width: 170,
    filterable: true,
    renderCell: (params) =>
      params.value ? (
        <Button
          variant='text'
          size='small'
          onClick={() =>
            window.open(
              `${getParentOrigin()}/license?l=${params.value}`,
              '_blank'
            )
          }
          endIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
          sx={{
            textTransform: 'none',
            p: 0,
            minWidth: 'auto',
            '&:hover': {
              textDecoration: 'underline',
              backgroundColor: 'transparent',
            },
          }}
        >
          {params.value}
        </Button>
      ) : null,
  },
  {
    field: 'license2Type',
    headerName: 'License 2 Type',
    width: 150,
    filterable: true,
  },
  {
    field: 'license2Role',
    headerName: 'License 2 Role',
    width: 130,
    filterable: true,
  },
  {
    field: 'license2Name',
    headerName: 'License 2 Name',
    width: 200,
    filterable: true,
  },
  {
    field: 'license2Exp',
    headerName: 'License 2 Exp',
    type: 'date',
    width: 130,
    filterable: true,
    valueGetter: (params) => {
      if (!params.row.license2Exp) return null
      const parts = params.row.license2Exp.split('/')
      if (parts.length !== 3) return null
      const month = parseInt(parts[0], 10) - 1
      const day = parseInt(parts[1], 10)
      const year = parseInt(parts[2], 10) + 2000
      return new Date(year, month, day)
    },
    valueFormatter: (params) => {
      if (!params.value) return ''
      const date = params.value as Date
      const month = date.getMonth() + 1
      const day = date.getDate()
      const year = date.getFullYear().toString().slice(-2)
      return `${month}/${day}/${year}`
    },
  },
]

function DomainSearch() {
  const apiRef = useGridApiRef()
  const [emailDomain, setEmailDomain] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [data, setData] = useState<TeacherData[] | null>(null)
  const [hasSearched, setHasSearched] = useState(false)

  const loadFromStorage = () => {
    try {
      const savedFilters = localStorage.getItem(STORAGE_KEY_FILTERS)
      const savedColumns = localStorage.getItem(STORAGE_KEY_COLUMNS)
      return {
        filters: savedFilters
          ? (JSON.parse(savedFilters) as GridFilterModel)
          : { items: [] },
        columns: savedColumns ? JSON.parse(savedColumns) : {},
      }
    } catch {
      return {
        filters: { items: [] },
        columns: {},
      }
    }
  }

  const { filters: initialFilters, columns: initialColumns } = loadFromStorage()
  const [filterModel, setFilterModel] =
    useState<GridFilterModel>(initialFilters)
  const [columnVisibilityModel, setColumnVisibilityModel] =
    useState(initialColumns)

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY_FILTERS, JSON.stringify(filterModel))
    } catch {
      // Ignore storage errors
    }
  }, [filterModel])

  useEffect(() => {
    try {
      localStorage.setItem(
        STORAGE_KEY_COLUMNS,
        JSON.stringify(columnVisibilityModel)
      )
    } catch {
      // Ignore storage errors
    }
  }, [columnVisibilityModel])

  // Read domain from parent URL on mount and auto-search if present
  useEffect(() => {
    const domainFromUrl = getParentUrlParam('domain')
    if (domainFromUrl) {
      setEmailDomain(domainFromUrl)
      // Trigger search after setting the domain
      const searchFromUrl = async () => {
        setLoading(true)
        setHasSearched(true)
        try {
          const parentOrigin = document.referrer
            ? new URL(document.referrer).origin
            : window.location.origin
          const apiUrl = `${parentOrigin}/api/domain-search`
          const response = await axios.get(apiUrl, {
            params: { emailDomain: domainFromUrl.trim() },
          })
          const fetchedData = (Object.values(response.data) || []) as Omit<
            TeacherData,
            'id' | 'firstName' | 'lastName' | 'title' | 'school'
          >[]
          const dataWithIds = fetchedData.map((row, index) => {
            const nameParts = (row.name || '').trim().split(/\s+/)
            const firstName = nameParts[0] || ''
            const lastName =
              nameParts.length > 1 ? nameParts.slice(1).join(' ') : ''
            return {
              ...row,
              id: row.code || `row-${index}`,
              firstName,
              lastName,
              title: '',
              school: '',
            }
          })
          setData(dataWithIds)
        } catch (err: unknown) {
          const errorMessage =
            (err as { response?: { data?: string }; message?: string }).response
              ?.data ||
            (err as { message?: string }).message ||
            'Failed to fetch domain search results'
          setError(errorMessage)
          setData([])
        } finally {
          setLoading(false)
        }
      }
      searchFromUrl()
    }
  }, [])

  const hasActiveFilters = filterModel.items.length > 0

  const applyFilters = (rows: TeacherData[], filters: GridFilterModel) => {
    if (!filters.items || filters.items.length === 0) return rows

    return rows.filter((row) => {
      return filters.items.every((filterItem) => {
        if (!filterItem.value && filterItem.value !== 0) return true

        const column = columns.find((col) => col.field === filterItem.field)
        if (!column) return true

        let value: unknown = row[filterItem.field as keyof TeacherData]
        if (column.valueGetter) {
          const params = { row, value }
          value = column.valueGetter(params as never)
        }

        if (value === null || value === undefined) {
          return (
            filterItem.operator === 'isEmpty' ||
            filterItem.operator === 'isNotEmpty'
          )
        }

        const filterValue = filterItem.value

        switch (filterItem.operator) {
          case 'contains':
            return String(value)
              .toLowerCase()
              .includes(String(filterValue).toLowerCase())
          case 'equals':
          case 'is':
            return String(value) === String(filterValue)
          case 'startsWith':
            return String(value)
              .toLowerCase()
              .startsWith(String(filterValue).toLowerCase())
          case 'endsWith':
            return String(value)
              .toLowerCase()
              .endsWith(String(filterValue).toLowerCase())
          case 'not':
            return String(value) !== String(filterValue)
          case 'isEmpty':
            return value === null || value === undefined || String(value) === ''
          case 'isNotEmpty':
            return value !== null && value !== undefined && String(value) !== ''
          case '=':
            return Number(value) === Number(filterValue)
          case '!=':
            return Number(value) !== Number(filterValue)
          case '>':
            return Number(value) > Number(filterValue)
          case '>=':
            return Number(value) >= Number(filterValue)
          case '<':
            return Number(value) < Number(filterValue)
          case '<=':
            return Number(value) <= Number(filterValue)
          default:
            return true
        }
      })
    })
  }

  const filteredData = useMemo(() => {
    if (!data) return []
    return applyFilters(data, filterModel)
  }, [data, filterModel])

  const getFilterDescription = () => {
    if (filterModel.items.length === 0) return ''
    return filterModel.items
      .filter((item) => item.value || item.value === 0)
      .map((item) => {
        const column = columns.find((col) => col.field === item.field)
        const columnName = column?.headerName || item.field
        return `${columnName} ${item.operator} "${item.value}"`
      })
      .join('\n')
  }

  const handleSearch = async (domainOverride?: string) => {
    const domain = domainOverride ?? emailDomain
    if (!domain.trim()) {
      setError('Please enter an email domain')
      return
    }

    if (domainOverride) {
      setEmailDomain(domainOverride)
    }

    setLoading(true)
    setError(null)
    setHasSearched(true)

    // Update parent URL with the search term
    updateParentUrl('domain', domain.trim())

    try {
      const parentOrigin = document.referrer
        ? new URL(document.referrer).origin
        : window.location.origin
      const apiUrl = `${parentOrigin}/api/domain-search`
      const response = await axios.get(apiUrl, {
        params: {
          emailDomain: domain.trim(),
        },
      })
      const fetchedData = (Object.values(response.data) || []) as Omit<
        TeacherData,
        'id' | 'firstName' | 'lastName' | 'title' | 'school'
      >[]
      const dataWithIds = fetchedData.map((row, index) => {
        const nameParts = (row.name || '').trim().split(/\s+/)
        const firstName = nameParts[0] || ''
        const lastName =
          nameParts.length > 1 ? nameParts.slice(1).join(' ') : ''
        return {
          ...row,
          id: row.code || `row-${index}`,
          firstName,
          lastName,
          title: '',
          school: '',
        }
      })
      setData(dataWithIds)
    } catch (err: unknown) {
      const errorMessage =
        (err as { response?: { data?: string }; message?: string }).response
          ?.data ||
        (err as { message?: string }).message ||
        'Failed to fetch domain search results'
      setError(errorMessage)
      setData([])
    } finally {
      setLoading(false)
    }
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch()
    }
  }

  const handleClear = () => {
    setEmailDomain('')
    setData(null)
    setHasSearched(false)
    // Remove domain from parent URL
    updateParentUrl('domain', null)
    setError(null)
    setFilterModel({ items: [] })
    setColumnVisibilityModel({})
  }

  const handleClearFilters = () => {
    setFilterModel({ items: [] })
    // Also clear DataGrid's internal filter state
    if (apiRef.current) {
      apiRef.current.setFilterModel({ items: [] })
    }
  }

  const handleExportCSV = (exportFiltered = false) => {
    const dataToExport = exportFiltered ? filteredData : data
    if (!dataToExport || dataToExport.length === 0) return

    // Export all columns, not just visible ones
    const headers = columns.map((col) => col.headerName || col.field)
    const rows = dataToExport.map((row) =>
      columns.map((col) => {
        const value = row[col.field as keyof TeacherData]
        if (value === null || value === undefined) return ''
        if (typeof value === 'string' && value.includes(',')) {
          return `"${value.replace(/"/g, '""')}"`
        }
        return String(value)
      })
    )

    const csvContent = [
      headers.join(','),
      ...rows.map((row) => row.join(',')),
    ].join('\n')

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    const url = URL.createObjectURL(blob)
    link.setAttribute('href', url)
    const suffix = exportFiltered ? '-filtered' : ''
    link.setAttribute(
      'download',
      `domain-search-${emailDomain}${suffix}-${
        new Date().toISOString().split('T')[0]
      }.csv`
    )
    link.style.visibility = 'hidden'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant='h4' sx={{ mb: 3, fontWeight: 600 }}>
        Domain Search
      </Typography>

      {!data ? (
        <Stack direction='row' spacing={2} alignItems='center' sx={{ mb: 3 }}>
          <TextField
            fullWidth
            label='Email Domain'
            placeholder='example.com'
            value={emailDomain}
            onChange={(e) => setEmailDomain(e.target.value)}
            onKeyPress={handleKeyPress}
            color='secondary'
            variant='outlined'
            sx={{ bgcolor: 'background.paper' }}
          />
          <Button
            variant='contained'
            onClick={() => handleSearch()}
            size='large'
            disabled={loading}
            startIcon={
              loading ? <CircularProgress size={20} /> : <SearchIcon />
            }
            sx={{ minWidth: 120 }}
          >
            {loading ? 'Searching...' : 'Search'}
          </Button>
        </Stack>
      ) : (
        <Stack
          direction='row'
          spacing={2}
          alignItems='center'
          justifyContent='space-between'
          sx={{ mb: 3 }}
        >
          <Typography variant='body1' sx={{ color: 'text.secondary' }}>
            Here are the results for <strong>{emailDomain}</strong>
          </Typography>
          <Button
            variant='contained'
            color='secondary'
            onClick={handleClear}
            size='medium'
            startIcon={<SearchIcon />}
          >
            New Search
          </Button>
        </Stack>
      )}

      {error && (
        <Alert severity='error' sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {data && data.length > 0 && (
        <Box sx={{ height: 600, mb: 3 }}>
          <Stack
            direction='row'
            justifyContent='space-between'
            alignItems='center'
            sx={{ mb: 2 }}
          >
            <Stack direction='row' alignItems='center' spacing={1}>
              <Typography variant='h6' sx={{ fontWeight: 600 }}>
                {hasActiveFilters
                  ? `Filtered Results (${filteredData.length})`
                  : `Results (${data.length})`}
              </Typography>
              {hasActiveFilters && (
                <Tooltip
                  title={
                    <Box sx={{ whiteSpace: 'pre-line' }}>
                      {getFilterDescription()}
                    </Box>
                  }
                  arrow
                >
                  <InfoOutlinedIcon
                    sx={{
                      fontSize: 18,
                      color: 'text.secondary',
                      cursor: 'help',
                    }}
                  />
                </Tooltip>
              )}
            </Stack>
            <Stack direction='row' spacing={1}>
              {hasActiveFilters && (
                <Button
                  variant='outlined'
                  startIcon={<FilterAltOffIcon />}
                  onClick={handleClearFilters}
                  size='small'
                >
                  Clear Filters
                </Button>
              )}
              {hasActiveFilters && (
                <Button
                  variant='outlined'
                  startIcon={<DownloadIcon />}
                  onClick={() => handleExportCSV(true)}
                  size='small'
                >
                  Export Filtered ({filteredData.length})
                </Button>
              )}
              <Button
                variant='outlined'
                startIcon={<DownloadIcon />}
                onClick={() => handleExportCSV(false)}
                size='small'
              >
                Export All ({data.length})
              </Button>
            </Stack>
          </Stack>
          <DataGrid
            apiRef={apiRef}
            rows={filteredData}
            columns={columns}
            onFilterModelChange={(newModel) => {
              if (newModel.items.length > 0) {
                const newItem = newModel.items[0]
                if (!newItem.value && newItem.value !== 0) {
                  // Filter value cleared - remove this field's filter
                  setFilterModel((prev) => ({
                    items: prev.items.filter(
                      (item) => item.field !== newItem.field
                    ),
                  }))
                } else {
                  // Add or update filter for this field
                  setFilterModel((prev) => {
                    const existingIndex = prev.items.findIndex(
                      (item) => item.field === newItem.field
                    )
                    if (existingIndex >= 0) {
                      const updatedItems = [...prev.items]
                      updatedItems[existingIndex] = newItem
                      return { items: updatedItems }
                    }
                    return { items: [...prev.items, newItem] }
                  })
                }
              }
            }}
            columnVisibilityModel={columnVisibilityModel}
            onColumnVisibilityModelChange={setColumnVisibilityModel}
            disableRowSelectionOnClick
            pageSizeOptions={[10, 25, 50, 100]}
            initialState={{
              pagination: {
                paginationModel: { pageSize: 100 },
              },
              sorting: {
                sortModel: [{ field: 'name', sort: 'asc' }],
              },
            }}
            sx={{
              '& .MuiDataGrid-columnHeaders': {
                backgroundColor: 'primary.main',
                color: 'primary.contrastText',
                fontWeight: 600,
              },
              '& .MuiDataGrid-columnHeaderTitle': {
                fontWeight: 600,
                color: 'primary.contrastText',
              },
              '& .MuiDataGrid-iconButtonContainer': {
                color: 'primary.contrastText',
              },
              '& .MuiDataGrid-menuIcon': {
                color: 'primary.contrastText',
              },
              '& .MuiDataGrid-filterIcon': {
                color: 'primary.contrastText',
              },
              '& .MuiDataGrid-sortIcon': {
                color: 'primary.contrastText',
              },
              '& .MuiDataGrid-columnHeader:focus': {
                outline: 'none',
              },
              '& .MuiDataGrid-columnHeader:focus-within': {
                outline: 'none',
              },
            }}
          />
        </Box>
      )}

      {!loading && hasSearched && !data?.length && !error && (
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant='body1' color='text.secondary'>
            No results found for domain: {emailDomain}
          </Typography>
        </Box>
      )}
    </Box>
  )
}

export default DomainSearch
